import { useContext, useMemo, useState } from 'react'

import { faUserGraduate, faKey, faEdit } from '@fortawesome/pro-solid-svg-icons'
import Cookie from 'js-cookie'
import { useSelector } from 'react-redux'
import { Tag } from 'antd'

import DataTable from '../../../../Components/antd/Tables/DataTable/DataTable'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import {
  getEmail,
  getEuropeanBirthDate, getFirstName, getIsActive, getLastLogin, getLastName, getMatricule, getNiss, getPhoneNumber, getStudentFullSection,
  getTranslatedGender, getTranslatedLanguage, getTranslatedNationality,
  Student,
  StudentProperties as _S
} from '../../../../utils/entities/student'
import { OrderBy } from '../../../../utils/constants'
import { isFunction } from 'lodash'
import { Action, DeleteAction } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import StudentInfo from '../../../../Components/shared/StudentInfo'
import AdminResetPassword from '../../../../Components/shared/AdminResetPassword'
import { StudentFormModalContext } from '../../../../Providers/Forms/StudentForm/StudentFormModal'
import { Actions } from '../../../../utils/http'
import { selectUser } from '../../../../reducers/UserReducer'

const Modes = Object.freeze({
  OPEN_SHEET: 1,
  RESET_PASSWORD: 2
})

const StudentsTable = ({ canResetPassword, canSeeAllInformations, data, exporter, loading, parameters, storageKey, onParametersChange, onChange, onDelete, onKeysChanged }) => {
  const { setFormHandler } = useContext(StudentFormModalContext)

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedStudent, setSelectedStudent] = useState({ data: null, mode: null })

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '240px' : '199px'})`, [Cookie.get('switch_user_id')])

  const columns = useMemo(() => {
    const columns = [
      { title: t('common.section'), orderBy: OrderBy.SECTIONS, key: s => getStudentFullSection(s, user.school), id: _S.SECTIONS },
      { title: t('common.lastname'), orderBy: OrderBy.LASTNAME, key: getLastName, id: _S.LASTNAME },
      { title: t('common.firstname'), orderBy: OrderBy.FIRSTNAME, key: getFirstName, id: _S.FIRSTNAME },
      { title: t('common.language'), orderBy: OrderBy.LANGUAGE, key: s => getTranslatedLanguage(s, t), id: _S.LANGUAGE },
      { title: t('common.email'), orderBy: OrderBy.EMAIL, key: getEmail, id: _S.EMAIL },
      { title: t('common.matricule'), orderBy: OrderBy.NISS, key: getMatricule, id: _S.MATRICULE },
      { title: t('common.phone_number'), orderBy: OrderBy.PHONE_NUMBER, key: getPhoneNumber, id: _S.PHONE_NUMBER },
      { title: t('common.birthdate'), orderBy: OrderBy.BIRTHDATE, key: getEuropeanBirthDate, id: _S.BIRTHDATE },
      { title: t('common.gender'), orderBy: OrderBy.GENDER, key: s => getTranslatedGender(s, t), id: _S.GENDER },
      { title: t('common.nationality'), orderBy: OrderBy.NATIONALITY, key: s => getTranslatedNationality(s, t), id: _S.NATIONALITY }
    ]

    if (canSeeAllInformations) {
      columns.push({ title: t('common.niss'), orderBy: OrderBy.NISS, key: getNiss, id: _S.NISS })
      columns.push({ title: t('common.last_login'), orderBy: OrderBy.LAST_LOGIN, key: getLastLogin, id: 'user.lastLogin' })
      columns.push({
        title: t('common.active'),
        key: s => {
          const isActive = getIsActive(s)

          return isActive ? <Tag color='green'> {t('common.yes')} </Tag> : <Tag color='red'> {t('common.no')} </Tag>
        },
        id: 'user.createdAt'
      })
    }

    return columns
  }, [t, canSeeAllInformations, user.school])

  const actions = useMemo(() => [
    new Action({
      title: 'students_table.actions.display_student_sheet',
      icon: faUserGraduate,
      onClick: i => setSelectedStudent({ data: i, mode: Modes.OPEN_SHEET })
    }),
    new Action({
      disabled: !canResetPassword,
      title: 'students_table.actions.reset_password',
      icon: faKey,
      onClick: i => setSelectedStudent({ data: i, mode: Modes.RESET_PASSWORD })
    }),
    new Action({
      title: 'common.edit',
      icon: faEdit,
      onClick: s => setFormHandler(form => ({ ...form, data: s, callback: s => onChange(s, Actions.UPDATE) }))
    }),
    new DeleteAction({
      disabled: !isFunction(onDelete),
      popconfirmTitle: 'students_table.actions.delete_student',
      onDelete: onDelete
    })
  ], [onDelete, onChange, canResetPassword])

  const handleClose = () => setSelectedStudent({ data: null, mode: null })

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        data={data}
        exporter={exporter}
        metadata={parameters.metadata}
        loading={loading}
        columns={columns}
        actions={actions}
        parameters={parameters}
        storageKey={storageKey}
        onCreate={() => setFormHandler(form => ({ ...form, data: new Student(), callback: onChange }))}
        onKeysChanged={onKeysChanged}
        onParametersChange={onParametersChange}
      />
      <StudentInfo student={selectedStudent.mode === Modes.OPEN_SHEET ? selectedStudent.data : null} onClose={handleClose} />
      {canResetPassword && (
        <AdminResetPassword
          userToResetPassword={selectedStudent.mode === Modes.RESET_PASSWORD ? selectedStudent.data : null}
          onClose={handleClose}
        />
      )}
    </div>
  )
}

export default StudentsTable
